import { useState } from "react";
import { ShieldCheck, ShieldAlert, Loader2, Search } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { verifyDraw, verifyPublic } from "@/lib/api";
import { TIPOS, TIPO_LABEL } from "@/lib/format";
import { toast } from "sonner";

export default function Verify() {
  return (
    <div data-theme="institutional">
      <section className="border-b border-border bg-card">
        <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-10">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-primary"><ShieldCheck className="h-4 w-4" /> Portal público de verificación</span>
          <h1 className="mt-1 font-display text-3xl font-bold">Verifica cualquier sorteo</h1>
          <p className="mt-2 max-w-2xl text-muted-foreground">Comprueba que el resultado es reproducible y que la semilla revelada corresponde al compromiso publicado antes del sorteo.</p>
        </div>
      </section>
      <section className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
        <Tabs defaultValue="id">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="id" data-testid="verify-tab-id">Por ID de sorteo</TabsTrigger>
            <TabsTrigger value="manual" data-testid="verify-tab-manual">Manual (desde acta)</TabsTrigger>
          </TabsList>
          <TabsContent value="id"><VerifyById /></TabsContent>
          <TabsContent value="manual"><VerifyManual /></TabsContent>
        </Tabs>
      </section>
    </div>
  );
}

const ResultAlert = ({ result }) => {
  if (!result) return null;
  return (
    <Alert className={result.pass ? "border-success/40 bg-emerald-50" : "border-destructive/40 bg-red-50"} data-testid="verification-result-status">
      {result.pass ? <ShieldCheck className="h-4 w-4 text-success" /> : <ShieldAlert className="h-4 w-4 text-destructive" />}
      <AlertTitle>{result.pass ? "Verificado ✓ — Resultado auténtico" : "No verificado — revisar datos"}</AlertTitle>
      <AlertDescription className="space-y-1 text-xs">
        <div>Compromiso: <strong>{result.commitment_ok ? "Válido" : "Inválido"}</strong></div>
        <div>Resultado recomputado: <strong>{result.resultado_ok ? "Coincide" : "No coincide"}</strong></div>
        {result.resultado_recomputado !== undefined && (
          <div className="font-mono">{JSON.stringify(result.resultado_recomputado)}</div>
        )}
      </AlertDescription>
    </Alert>
  );
};

function VerifyById() {
  const [id, setId] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!id.trim()) return;
    setLoading(true); setResult(null);
    try {
      const v = await verifyDraw(id.trim());
      if (v.verificable === false) { toast.warning(v.motivo || "No verificable aún"); setResult(null); }
      else setResult(v);
    } catch {
      toast.error("Sorteo no encontrado");
    } finally { setLoading(false); }
  };

  return (
    <Card className="mt-4">
      <CardHeader><CardTitle className="font-display text-lg">Verificar por identificador</CardTitle></CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label htmlFor="draw-id">ID del sorteo</Label>
            <Input id="draw-id" value={id} onChange={(e) => setId(e.target.value)} placeholder="p. ej. 35dc2c59-…" data-testid="verify-id-input" />
          </div>
          <Button type="submit" className="gap-2" disabled={loading} data-testid="verify-id-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Verificar
          </Button>
        </form>
        <div className="mt-4"><ResultAlert result={result} /></div>
      </CardContent>
    </Card>
  );
}

function VerifyManual() {
  const [form, setForm] = useState({ tipo: "lotto", parametros: '{"n":49,"k":6}', server_seed: "", client_seed: "", nonce: "", commitment: "", resultado_esperado: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setResult(null);
    try {
      const payload = {
        tipo: form.tipo,
        parametros: form.parametros ? JSON.parse(form.parametros) : {},
        server_seed: form.server_seed.trim(),
        client_seed: form.client_seed.trim(),
        nonce: parseInt(form.nonce, 10),
        commitment: form.commitment.trim() || null,
        resultado_esperado: form.resultado_esperado ? JSON.parse(form.resultado_esperado) : null,
      };
      const v = await verifyPublic(payload);
      setResult(v);
    } catch (err) {
      toast.error("Datos inválidos. Revisa el JSON de parámetros/resultado.");
    } finally { setLoading(false); }
  };

  return (
    <Card className="mt-4">
      <CardHeader><CardTitle className="font-display text-lg">Verificar manualmente</CardTitle></CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Tipo de juego</Label>
              <Select value={form.tipo} onValueChange={(v) => set("tipo", v)}>
                <SelectTrigger data-testid="verify-manual-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>{TIPOS.map((t) => <SelectItem key={t} value={t}>{TIPO_LABEL[t]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="nonce">Nonce</Label>
              <Input id="nonce" value={form.nonce} onChange={(e) => set("nonce", e.target.value)} placeholder="42" data-testid="verify-manual-nonce" />
            </div>
          </div>
          <div>
            <Label htmlFor="parametros">Parámetros (JSON)</Label>
            <Textarea id="parametros" value={form.parametros} onChange={(e) => set("parametros", e.target.value)} className="font-mono text-xs" rows={2} />
          </div>
          <div>
            <Label htmlFor="server_seed">Semilla del servidor (revelada)</Label>
            <Input id="server_seed" value={form.server_seed} onChange={(e) => set("server_seed", e.target.value)} className="font-mono text-xs" data-testid="verify-manual-server-seed" />
          </div>
          <div>
            <Label htmlFor="client_seed">Semilla del cliente</Label>
            <Input id="client_seed" value={form.client_seed} onChange={(e) => set("client_seed", e.target.value)} className="font-mono text-xs" data-testid="verify-manual-client-seed" />
          </div>
          <div>
            <Label htmlFor="commitment">Compromiso publicado (opcional)</Label>
            <Input id="commitment" value={form.commitment} onChange={(e) => set("commitment", e.target.value)} className="font-mono text-xs" data-testid="verification-form-commitment-input" />
          </div>
          <div>
            <Label htmlFor="resultado">Resultado esperado (JSON, opcional)</Label>
            <Input id="resultado" value={form.resultado_esperado} onChange={(e) => set("resultado_esperado", e.target.value)} className="font-mono text-xs" placeholder='[13,17,24,39,45,47]' />
          </div>
          <Button type="submit" className="gap-2" disabled={loading} data-testid="verify-manual-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Verificar
          </Button>
        </form>
        <div className="mt-4"><ResultAlert result={result} /></div>
      </CardContent>
    </Card>
  );
}
